import { Injectable } from '@angular/core';
import { Drivers, Storage } from '@ionic/storage';
import { ReplaySubject, Subject } from 'rxjs';
import { BrandModel } from '../models/brand.model';
import { UserModel } from '../models/user.model';
import { logError } from '../utils/log-error';
import { PlanModel } from '../models/plan.model';

export interface ISettingsFunction<T> {
  (value?: T): Promise<T>;
  $: Subject<T>;
};

function wrap<T>(store: Promise<Storage>, key: string) {
  const subject = new ReplaySubject<T>(1);

  // initialize subject
  store.then(x => x.get(key))
    .then(x => subject.next(x));

  const method: ISettingsFunction<T> = Object.assign(
    async (value?: T) => {
      const impl = await store;

      if (value === undefined)
        return (await impl.get(key)) as T;

      await impl.set(key, value);
      subject.next(value);

      return value;
    },
    { $: subject }
  );
  return method;
}

@Injectable({
  providedIn: 'root'
})
export class SettingsService {

  private _store = SettingsService.createStorage();

  private static createStorage() {
    try {
      return new Storage({ driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage] }).create();
    } catch (err) {
      logError('Error creating storage in SettingsService', err);
      throw err;
    }
  }

  constructor() {
  }

  authToken = wrap<string>(this._store, 'authToken');
  user = wrap<UserModel>(this._store, 'user');
  brand = wrap<BrandModel>(this._store, 'brand');
  plan = wrap<PlanModel>(this._store, 'plan');
  lastTranslationUpdate = wrap<number>(this._store, 'lastTranslationUpdate');
  language = wrap<string>(this._store, 'language');

  async reset() {
    const store = await this._store;
    await store.clear();
  }

  async getByKey<T>(key: string): Promise<T | null> {
    const store = await this._store;
    return store.get(key);
  }

  async setByKey<T>(key: string, value: T): Promise<void> {
    const store = await this._store;
    await store.set(key, value);
  }

  async removeByKey(key: string): Promise<void> {
    const store = await this._store;
    await store.remove(key);
  }

  async forEach(callback: (value: any, key: string) => void): Promise<void> {
    const store = await this._store;
    await store.forEach(callback);
  }
}
